export type ResultColumnKey =
  | 'lifter'
  | 'competition_type'
  | 'age_category'
  | 'body_weight'
  | 'squat'
  | 'benchpress'
  | 'deadlift'
  | 'group'
  | 'team'
  | 'lot'
  | 'rank'
  | 'is_coef_group'
  | 'disqualified'
  | 'save';

export interface ResultColumn {
  key: ResultColumnKey;
  /** Full name — used for tooltips, the mobile/standalone per-field labels and aria. */
  label: string;
  /** Abbreviated header shown in the dense desktop column header. Falls back to `label`. */
  short?: string;
  /** Minimum track width in px; also used to compute the grid's horizontal-scroll min-width. */
  min: number;
  /** When true the column absorbs leftover space on wide screens instead of staying fixed. */
  flex?: boolean;
  align?: 'center';
}

/** Matches the column-gap in result-grid.module.css; used to compute the scroll min-width. */
export const RESULT_GRID_GAP = 6;

/**
 * The visible columns depend on the competition: individual comps pick competition type / age
 * category per lifter, team comps pick a team instead. Header and rows render from this single
 * source so their tracks always line up. Widths are kept tight so the full row fits the page
 * without horizontal scrolling on a standard-width screen.
 */
export function getResultColumns({
  isTeamComp,
  isSuperUser,
}: {
  isTeamComp: boolean;
  isSuperUser: boolean;
}): ResultColumn[] {
  const columns: ResultColumn[] = [{ key: 'lifter', label: 'Lyftare', min: 176, flex: true }];

  if (!isTeamComp) {
    columns.push({ key: 'competition_type', label: 'Tävlingstyp', min: 128 });
    columns.push({ key: 'age_category', label: 'Ålderskategori', short: 'Ålder', min: 134 });
  }

  columns.push({ key: 'body_weight', label: 'Kroppsvikt', short: 'Kroppsv.', min: 66 });
  columns.push({ key: 'squat', label: 'Knäböj', min: 60 });
  columns.push({ key: 'benchpress', label: 'Bänkpress', short: 'Bänkpr.', min: 60 });
  columns.push({ key: 'deadlift', label: 'Marklyft', min: 60 });
  columns.push({ key: 'group', label: 'Grupp', min: 82 });

  if (isTeamComp) {
    // Team comps have far fewer columns, so let Lag flex and share the slack — team names are long.
    columns.push({ key: 'team', label: 'Lag', min: 200, flex: true });
  }

  columns.push({ key: 'lot', label: 'Lott', min: 54 });

  if (isSuperUser && !isTeamComp) {
    columns.push({ key: 'rank', label: 'Placering', short: 'Plac.', min: 62 });
  }

  if (!isTeamComp) {
    columns.push({ key: 'is_coef_group', label: 'Koefficientgrupp', short: 'Koeff.', min: 50, align: 'center' });
  }

  columns.push({ key: 'disqualified', label: 'Ej godkänd', short: 'Ej godk.', min: 58, align: 'center' });
  columns.push({ key: 'save', label: '', min: 84 });

  return columns;
}

export function resultGridTemplate(columns: ResultColumn[]): string {
  return columns.map(c => (c.flex ? `minmax(${c.min}px, 1.4fr)` : `${c.min}px`)).join(' ');
}

export function resultGridMinWidth(columns: ResultColumn[]): number {
  return columns.reduce((sum, c) => sum + c.min, 0) + RESULT_GRID_GAP * (columns.length - 1);
}
